import { pushGtmEvent } from '@/src/analytics/gtm'
import itineraryApi from '@/src/api/rtkQuery/itineraryApi'
import useGetItineraryId from '@/src/hooks/itinerary/useGetItineraryId'
import usePriceCalculation from '@/src/hooks/itinerary/usePriceCalculation'
import { RootState } from '@/src/redux/rootReducer'
import { useAppSelector } from '@/src/redux/store'

import { extractFlightInfo } from '@/src/utils/analytics/clevertap/itinerary'
import { getDeviceType } from '@/src/utils/api/getDeviceType'
import { getFormattedDate } from '@/src/utils/date'
import { getPlatformName } from '@/src/utils/deviceType'
import { isUserSignedIn } from '@/src/utils/generalUtils/checkUserLogin'
import { useLocale, useTranslations } from 'next-intl'
import { useMemo } from 'react'

const useTravelersGtmEvent = () => {
    const locale = useLocale()
    const t = useTranslations('metadata')
    const deviceType = getDeviceType()
    const platform = getPlatformName(deviceType)
    const itineraryId = useGetItineraryId()
    const isSignedIn = isUserSignedIn()
    const data = useAppSelector((state: RootState) => {
        const cachedData = itineraryApi.endpoints.getItineraryData.select({
            itineraryId
        })(state)
        return cachedData?.status === 'fulfilled' ? cachedData.data : null
    })
    const { totalPrice } = usePriceCalculation()

    // Destructure main data objects
    const { flights = [], searchCriteria, itineraryData } = data || {}
    const { currencyConversionFactor, couponDetails } = itineraryData || {}
    const { paxInfo, departDate, arrivalDate } = searchCriteria || {}
    const { adults = 0, children = 0, infants = 0 } = paxInfo || {}

    const { airline, airlineName, flightIds, cabinType, flightRouteStops } = useMemo(
        () => extractFlightInfo(flights, '|'),
        [flights]
    )
    const origin = flights[0]?.from || ''
    const fromCity = flights[0]?.fromCity || ''
    const destination = flights[0]?.to || ''
    const toCity = flights[0]?.toCity || ''
    const tripType = arrivalDate ? 'round_trip' : 'one_way_trip'
    const departureDate = departDate?.split('T')[0] || ''
    const returnDate = arrivalDate?.split('T')[0] || ''
    const item = `${origin}-${destination}-${arrivalDate ? 'rt' : 'ow'}`

    const pushEventToDataLayer = (payload: any) => {
        // TODO: When writing for Cleartrip, we can define a function here that creates the payload.
        // Based on it, for Cleartrip or Flyin we can have two functions and we will use that to get the payload.

        const finalPayload = {
            event: 'click_flight_payment_details',
            page_domain: window.location.hostname,
            platform,
            price: totalPrice,
            item,
            is_new_flow: 'yes',
            trip_type: tripType,
            source_airport_city: fromCity,
            destination_airport_city: toCity,
            source_airport_code: origin.toLowerCase(),
            destination_airport_code: destination.toLowerCase(),
            travelers_adult_count: adults,
            travelers_children_count: children,
            travelers_infants_count: infants,
            travelers_total_count: adults + children + infants,
            departing_departure_date: getFormattedDate(departureDate, 'YYYY/MM/DD'),
            returning_departure_date: returnDate
                ? getFormattedDate(returnDate, 'YYYY/MM/DD')
                : 'no_returning_departure_date',
            flight_class: cabinType,
            product_category: 'flights',
            page_referrer: document.referrer || 'no_page_referrer',
            page_language: locale,
            page_location: window.location.href,
            page_category: 'checkout',
            page_sub_category: 'flights',
            page_path: window.location.pathname,
            page_title: t('title'),
            page_country: window.location.hostname === 'www.flyin.com' ? 'global' : 'eg',
            page_currency: currencyConversionFactor?.code?.toLowerCase() || '',

            currency: currencyConversionFactor?.code?.toUpperCase() || '',
            Airline_code: airline,
            Airline_Name: airlineName,
            airline_names: airlineName,
            flight_ids: flightIds,
            flight_route_stops: flightRouteStops,
            booking_flight_total_cost: totalPrice,
            user_logged_in: isSignedIn ? 'yes' : 'no',
            booking_coupon_code: couponDetails?.[0]?.couponCode || 'n/a',
            ...payload
        }

        pushGtmEvent({ ...finalPayload, event: 'click_flight_payment_details' })
    }

    return {
        pushEventToDataLayer
    }
}

export default useTravelersGtmEvent
